import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { 
  Users, BarChart3, AlertTriangle, Upload, Settings, 
  X, ChevronLeft, ChevronRight, Sparkles, Building, Mail, Shield
} from "lucide-react";

const AdminTutorial = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const steps = [
    {
      title: "Bienvenue sur SophIA Admin !",
      description: "Découvrez comment piloter l'utilisation de SophIA dans votre établissement. Ce tutoriel vous guidera à travers les fonctionnalités clés du tableau de bord administrateur.",
      icon: Sparkles,
      color: "from-[#D4AF37] to-[#B8960C]",
      tips: [
        "Suivez l'activité de vos élèves en temps réel",
        "Gérez les alertes de modération",
        "Accédez aux statistiques d'utilisation"
      ]
    },
    {
      title: "Vue d'ensemble",
      description: "Le tableau de bord vous donne une vision globale de l'activité : nombre d'élèves actifs, exercices complétés, score moyen, et alertes en attente.",
      icon: BarChart3,
      color: "from-[#1A365D] to-[#2D4A6F]",
      tips: [
        "Les statistiques sont mises à jour en temps réel",
        "Cliquez sur les cartes pour voir le détail",
        "Exportez les données en PDF si besoin"
      ]
    },
    {
      title: "Gestion des élèves",
      description: "Consultez la liste de tous les élèves, leur progression, et leur niveau. Utilisez les QR codes pour une inscription rapide en classe !",
      icon: Users,
      color: "from-[#3B82F6] to-[#1D4ED8]",
      tips: [
        "QR codes par classe pour inscription rapide",
        "Triez par points, niveau ou activité récente",
        "Observez les conversations pédagogiques"
      ]
    },
    {
      title: "Import en masse",
      description: "Importez facilement vos listes d'élèves via Excel ou CSV. Le système crée automatiquement les comptes et génère les mots de passe.",
      icon: Upload,
      color: "from-[#22C55E] to-[#16A34A]",
      tips: [
        "Format accepté : Excel (.xlsx) ou CSV",
        "Colonnes requises : Prénom, Nom, Classe",
        "Les mots de passe sont générés automatiquement"
      ]
    },
    {
      title: "Alertes & Modération",
      description: "Surveillez les alertes et lisez les conversations des élèves (observation pédagogique). Soyez notifié par email en cas d'incident.",
      icon: AlertTriangle,
      color: "from-[#EF4444] to-[#DC2626]",
      tips: [
        "Page 'Observation' pour lire les chats",
        "Les incidents critiques déclenchent un email",
        "Débats multi-philosophes visibles aussi"
      ]
    },
    {
      title: "Gestion Multi-établissements",
      description: "Si vous gérez plusieurs établissements, basculez facilement entre eux. Chaque établissement a ses propres statistiques et élèves.",
      icon: Building,
      color: "from-[#8B5CF6] to-[#7C3AED]",
      tips: [
        "Sélectionnez l'établissement dans le menu",
        "Comparez les performances entre établissements",
        "Créez de nouveaux établissements si besoin"
      ]
    },
    {
      title: "Notifications Email",
      description: "Configurez les notifications email pour recevoir des alertes automatiques : incidents de modération, élèves inactifs, rapports hebdomadaires.",
      icon: Mail,
      color: "from-[#F59E0B] to-[#D97706]",
      tips: [
        "Vérifiez la configuration SMTP dans les réglages",
        "Testez l'envoi d'email avec le bouton 'Test'",
        "Personnalisez les types d'alertes reçues"
      ]
    },
    {
      title: "Vous êtes prêt !",
      description: "Vous connaissez maintenant les fonctionnalités essentielles. N'hésitez pas à explorer le tableau de bord et à contacter le support si vous avez des questions.",
      icon: Shield,
      color: "from-[#1A365D] to-[#D4AF37]",
      tips: [
        "Consultez le 'Mode d'emploi' pour plus de détails",
        "Le support est disponible par email",
        "Vos retours nous aident à améliorer SophIA"
      ]
    }
  ];

  const handleComplete = () => {
    localStorage.setItem("sophia_admin_tutorial_completed", "true");
    setIsVisible(false);
    setTimeout(() => onComplete(), 300);
  };

  const handleSkip = () => {
    localStorage.setItem("sophia_admin_tutorial_completed", "true");
    setIsVisible(false);
    setTimeout(() => onSkip(), 300);
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      data-testid="admin-tutorial-overlay"
    >
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-gray-200">
          <div 
            className="h-full bg-gradient-to-r from-[#1A365D] to-[#D4AF37] transition-all duration-500"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Skip button */}
        <button 
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors z-10"
          aria-label="Passer le tutoriel"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6 md:p-8">
          {/* Icon */}
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${currentStepData.color} flex items-center justify-center mb-6 shadow-lg`}>
            <Icon className="w-8 h-8 text-white" />
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-medium text-[#D4AF37]">
              Étape {currentStep + 1} sur {steps.length}
            </span>
          </div>

          {/* Title */}
          <h2 
            className="text-2xl font-bold text-[#1A365D] mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {currentStepData.title}
          </h2>

          {/* Description */}
          <p className="text-[#64748B] mb-6 leading-relaxed">
            {currentStepData.description}
          </p>

          {/* Tips */}
          <div className="bg-[#F8F9FA] rounded-xl p-4 mb-6">
            <h4 className="text-sm font-semibold text-[#1A365D] mb-2">
              À retenir :
            </h4>
            <ul className="space-y-2">
              {currentStepData.tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-[#64748B]">
                  <span className="text-[#D4AF37] mt-1">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="text-[#64748B] hover:text-[#1A365D]"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Précédent
            </Button>

            <div className="flex gap-1.5">
              {steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentStep 
                      ? 'bg-[#1A365D] w-6' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            <Button
              onClick={nextStep}
              className="bg-[#1A365D] hover:bg-[#132745]"
            >
              {currentStep === steps.length - 1 ? "Terminer" : "Suivant"}
              {currentStep < steps.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTutorial;
